'use client'

import { motion, useReducedMotion } from 'framer-motion'
import {
  Children,
  type ComponentProps,
  createContext,
  isValidElement,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  CartesianGrid,
  Bar as RechartsBar,
  BarChart as RechartsBarChart,
  XAxis as RechartsXAxis,
  YAxis as RechartsYAxis,
  Rectangle,
  ReferenceLine,
  useActiveTooltipDataPoints,
} from 'recharts'
import { RectRadius } from 'recharts/types/shape/Rectangle'

import {
  type BackgroundVariant,
  ChartBackground,
} from '@/components/evilcharts/recharts-background'
import {
  Brush,
  type BrushProps,
  EvilBrush,
  type EvilBrushRange,
  useEvilBrush,
} from '@/components/evilcharts/recharts-brush'
import {
  type ChartConfig,
  ChartContainer,
  getColorsCount,
  getLoadingData,
  LoadingIndicator,
} from '@/components/evilcharts/recharts-chart'
import {
  ChartLegend,
  ChartLegendContent,
  type ChartLegendVariant,
} from '@/components/evilcharts/recharts-legend'
import {
  ChartTooltip,
  ChartTooltipContent,
  type TooltipRoundness,
  type TooltipVariant,
} from '@/components/evilcharts/recharts-tooltip'

// Constants
const DEFAULT_BAR_RADIUS = 2
const LOADING_BAR_DATA_KEY = 'loading'
const LOADING_ANIMATION_DURATION = 2000 // in milliseconds
const STACK_ID = 'evil-stacked'
const BAR_GROW_DURATION = 0.5 // per-bar grow-in length, in seconds
const BAR_STAGGER = 0.05 // delay between consecutive bars, in seconds
const REVEAL_EASE: [number, number, number, number] = [0, 0.7, 0.5, 1] // grow-in easing

type BarVariant =
  | 'default'
  | 'hatched'
  | 'duotone'
  | 'duotone-reverse'
  | 'gradient'
  | 'stripped'
  | 'expandable'

// `expandable` (ported from the ECharts twin): every bar keeps its full geometry but paints
// only a centred strip across its thickness, so it reads as a thin line; the hovered row's
// strip eases out to full thickness and back on leave. Same constants as the ECharts twin.
const EXPAND_COLLAPSED = 0.2 // resting strip thickness, as a fraction of the bar
const EXPAND_TAU = 70 // ease time-constant, in milliseconds (exponential approach)
type StackType = 'default' | 'stacked' | 'percent'
type BarLayout = 'vertical' | 'horizontal'

/**
 * Order in which bars grow into view. Recharts' own bar animation is permanently
 * disabled — every bar instead grows from its baseline (bottom for vertical
 * layout, left for horizontal), and this controls the stagger sequence.
 *
 * NOTE: the grow-in is a per-frame animation, so it is heavier than a static
 * chart. `"none"` opts out entirely; it is also what a device with the OS
 * "reduce motion" preference falls back to automatically.
 */
type BarAnimationType = 'none' | 'left-to-right' | 'right-to-left' | 'center-out' | 'edges-in'

// ─────────────────────────────────────────────────────────────────────────────
// Shared context
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Shared state for every part of the chart. Lifted into <EvilBarChart /> so that
 * <Bar />, <XAxis />, <Legend />, and friends can read it without prop drilling.
 * Sub-components are composed freely — the provider is the single source of truth.
 */
type BarChartContextValue = {
  config: ChartConfig // colors + labels for every series
  isStacked: boolean // whether bars stack on top of each other
  isHorizontal: boolean // whether bars are laid out horizontally
  isLoading: boolean // whether the chart shows its loading skeleton
  barRadius: number // default corner radius each <Bar /> inherits
  animationType: BarAnimationType // default grow-in order each <Bar /> inherits
  introStartedAt: number // timestamp the chart mounted — anchors the one-shot grow-in
  dataLength: number // number of rows currently rendered
  data: readonly Record<string, unknown>[] // rows currently rendered (maps tooltip → index)
  selectedDataKey: string | null // currently selected series, or null when none
  selectDataKey: (dataKey: string | null) => void // sets the selected series
  isMouseInChart: boolean // whether the pointer is currently over the chart
}

const BarChartContext = createContext<BarChartContextValue | null>(null)

// Reads the chart context, throwing a helpful error when used outside <EvilBarChart />
function useBarChart() {
  const context = use(BarChartContext)

  if (!context) {
    throw new Error('Bar chart parts (<Bar />, <XAxis />, …) must be used within <EvilBarChart />')
  }

  return context
}

// ─────────────────────────────────────────────────────────────────────────────
// Root container
// ─────────────────────────────────────────────────────────────────────────────

// Validates that every config key also exists on the data row type
type ValidateConfigKeys<TData, TConfig> = {
  [K in keyof TConfig]: K extends keyof TData ? ChartConfig[string] : never
}

type EvilBarChartBaseProps<
  TData extends Record<string, unknown>,
  TConfig extends Record<string, ChartConfig[string]>,
> = {
  config: TConfig & ValidateConfigKeys<TData, TConfig> // series colors + labels
  data: TData[] // rows rendered by the chart
  children: ReactNode // composed parts — <Bar />, <XAxis />, <Legend />, …
  className?: string // extra classes for the chart container
  chartProps?: ComponentProps<typeof RechartsBarChart> // escape hatch for the raw Recharts chart
  stackType?: StackType // how multiple bars combine
  layout?: BarLayout // orientation of the bars
  barRadius?: number // default corner radius for every <Bar />
  animationType?: BarAnimationType // default grow-in order for every <Bar />
  barGap?: number // gap between bars within the same category
  barCategoryGap?: number // gap between categories of bars
  backgroundVariant?: BackgroundVariant // background pattern drawn behind the chart
  defaultSelectedDataKey?: string | null // series selected on first render
  onSelectionChange?: (selectedDataKey: string | null) => void // fires when the selected series changes
  isLoading?: boolean // shows the animated loading skeleton
  loadingBars?: number // number of bars in the loading skeleton
  xDataKey?: keyof TData & string // x-axis key — only needed for the <Brush /> footer
}

type EvilBarChartProps<
  TData extends Record<string, unknown>,
  TConfig extends Record<string, ChartConfig[string]>,
> = EvilBarChartBaseProps<TData, TConfig>

/**
 * Root of the composible bar chart. Owns the data, the shared context, the
 * loading skeleton, and the optional zoom brush. Everything visual — axes,
 * grid, tooltip, legend, and the bars themselves — is composed as children,
 * so a consumer renders exactly the parts they need.
 */
export function EvilBarChart<
  TData extends Record<string, unknown>,
  TConfig extends Record<string, ChartConfig[string]>,
>({
  config,
  data,
  children,
  className,
  chartProps,
  stackType = 'default',
  layout = 'vertical',
  barRadius = DEFAULT_BAR_RADIUS,
  animationType = 'left-to-right',
  barGap,
  barCategoryGap,
  backgroundVariant,
  defaultSelectedDataKey = null,
  onSelectionChange,
  isLoading = false,
  loadingBars,
  xDataKey,
}: EvilBarChartProps<TData, TConfig>) {
  const chartId = useId().replace(/:/g, '') // colon-free id keeps CSS/SVG selectors valid
  // Anchors the grow-in to a fixed moment so it plays exactly once — re-renders
  // and Recharts' bar remounts read elapsed time from here instead of replaying.
  // Lazy useState stamps the time once, on the initial render only.
  const [introStartedAt] = useState(() => Date.now())
  const [selectedDataKey, setSelectedDataKey] = useState<string | null>(defaultSelectedDataKey)
  const [isMouseInChart, setIsMouseInChart] = useState(false)
  const { loadingData, onShimmerExit } = useLoadingData(isLoading, loadingBars)
  const { visibleData, brushProps } = useEvilBrush({ data })

  // Brush is a <Brush /> child now (not props): pull it out of the children so
  // it never reaches the Recharts tree, and drive the footer from its props.
  const brush = useMemo(() => {
    // Pull the <Brush> element out of the children (config-only, never rendered
    // into the Recharts tree); toArray also assigns stable keys to the rest.
    const parts = Children.toArray(children)
    const brushEl = parts.find((child) => isValidElement(child) && child.type === Brush)
    const bp = (isValidElement(brushEl) ? brushEl.props : {}) as BrushProps
    return {
      slot: {
        present: isValidElement(brushEl),
        height: bp.height,
        formatLabel: bp.formatLabel,
        onChange: bp.onChange,
      },
      chartChildren: parts.filter((child) => !(isValidElement(child) && child.type === Brush)),
    }
  }, [children])
  const showBrush = brush.slot.present

  const isStacked = stackType === 'stacked' || stackType === 'percent'
  const isHorizontal = layout === 'horizontal'
  const displayData = showBrush && !isLoading ? visibleData : data

  // Updates selection state and notifies the parent
  const selectDataKey = useCallback(
    (newSelectedDataKey: string | null) => {
      setSelectedDataKey(newSelectedDataKey)
      onSelectionChange?.(newSelectedDataKey)
    },
    [onSelectionChange]
  )

  const contextValue = useMemo<BarChartContextValue>(
    () => ({
      config,
      isStacked,
      isHorizontal,
      isLoading,
      barRadius,
      animationType,
      introStartedAt,
      dataLength: displayData.length,
      data: displayData,
      selectedDataKey,
      selectDataKey,
      isMouseInChart,
    }),
    [
      config,
      isStacked,
      isHorizontal,
      isLoading,
      barRadius,
      animationType,
      introStartedAt,
      displayData,
      selectedDataKey,
      selectDataKey,
      isMouseInChart,
    ]
  )

  return (
    <BarChartContext value={contextValue}>
      <ChartContainer
        className={className}
        config={config}
        footer={
          showBrush &&
          !isLoading && (
            <EvilBrush
              data={data}
              chartConfig={config}
              xDataKey={xDataKey}
              variant="bar"
              barRadius={barRadius}
              height={brush.slot.height}
              formatLabel={brush.slot.formatLabel}
              stacked={isStacked}
              skipStyle
              className="mt-1"
              {...brushProps}
              onChange={(range) => {
                brushProps.onChange(range)
                brush.slot.onChange?.(range)
              }}
            />
          )
        }
      >
        <LoadingIndicator isLoading={isLoading} />
        <RechartsBarChart
          id={chartId}
          accessibilityLayer
          layout={isHorizontal ? 'vertical' : 'horizontal'}
          data={isLoading ? loadingData : displayData}
          barGap={barGap}
          barCategoryGap={barCategoryGap}
          stackOffset={stackType === 'percent' ? 'expand' : undefined}
          onMouseEnter={() => setIsMouseInChart(true)}
          onMouseLeave={() => setIsMouseInChart(false)}
          {...chartProps}
        >
          {backgroundVariant && <ChartBackground variant={backgroundVariant} />}
          <ReferenceLine color="white" />
          {brush.chartChildren}
          {isLoading && <LoadingBar chartId={chartId} onShimmerExit={onShimmerExit} />}
        </RechartsBarChart>
      </ChartContainer>
    </BarChartContext>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Composible parts
// ─────────────────────────────────────────────────────────────────────────────

type BarProps = {
  dataKey: string // series key — must exist on the data and config
  variant?: BarVariant // fill style for this bar only
  radius?: number // corner radius — falls back to the chart default
  animationType?: BarAnimationType // grow-in order — falls back to the chart default
  isClickable?: boolean // lets this bar be selected by clicking it
  enableHoverHighlight?: boolean // dims this bar while another bar is hovered
  glowing?: boolean // applies a soft outer glow to this bar
  bufferBar?: boolean // renders the last data point as a hatched "buffer" bar
  barProps?: ComponentProps<typeof RechartsBar> // escape hatch for raw Recharts Bar props
}

/**
 * A single bar series. Each <Bar /> is fully self-contained: it generates its
 * own gradient/pattern definitions under a unique id, so any number of bars —
 * each with its own variant, radius, glow, and clickability — can live in one
 * chart without style collisions.
 */
function Bar({
  dataKey,
  variant = 'default',
  radius,
  animationType,
  isClickable = false,
  enableHoverHighlight = false,
  glowing = false,
  bufferBar = false,
  barProps,
}: BarProps) {
  const {
    config,
    isStacked,
    isHorizontal,
    isLoading,
    barRadius: defaultRadius,
    animationType: defaultAnimation,
    introStartedAt,
    dataLength,
    selectedDataKey,
    selectDataKey,
    isMouseInChart,
    data,
  } = useBarChart()
  const id = useId().replace(/:/g, '') // unique id scopes this bar's style defs
  // Devices set to "reduce motion" skip the grow-in animation entirely
  const shouldReduceMotion = useReducedMotion()
  // The hovered row comes from the chart's own tooltip state (needs a <Tooltip />), so the
  // whole row band is the hover target — not just the thin resting strip.
  const activePoint = useActiveTooltipDataPoints<Record<string, unknown>>()?.[0]
  const hoveredIndex = activePoint ? data.indexOf(activePoint) : -1
  const expand = useExpandProgress(
    variant === 'expandable' && !isLoading,
    hoveredIndex < 0 ? null : hoveredIndex,
    !!shouldReduceMotion
  )

  // The root renders the skeleton bar while loading, so real bars step aside
  if (isLoading) return null

  const resolvedRadius = radius ?? defaultRadius
  const isSelected = selectedDataKey === dataKey

  // The grow-in is a per-frame animation — heavier than a static chart — so
  // `"none"` and the OS reduce-motion preference both opt out of it.
  const revealType: BarAnimationType = shouldReduceMotion
    ? 'none'
    : (animationType ?? defaultAnimation)

  const customBarProps = {
    id,
    dataKey,
    variant,
    barRadius: resolvedRadius,
    glowing,
    bufferBar,
    isClickable,
    enableHoverHighlight,
    isMouseInChart,
    isHorizontal,
    introStartedAt,
    selectedDataKey,
    dataLength,
    expandOf: (index: number) => expand.get(index) ?? EXPAND_COLLAPSED,
    onClick: () => {
      if (!isClickable) return
      // Clicking the selected bar clears the selection, otherwise selects it
      selectDataKey(isSelected ? null : dataKey)
    },
  }

  return (
    <>
      <RechartsBar
        dataKey={dataKey}
        stackId={isStacked ? STACK_ID : undefined}
        fill={`url(#${id}-colors-${dataKey})`}
        radius={resolvedRadius}
        // Recharts' built-in bar animation is permanently disabled — every bar
        // instead grows in from its baseline via the staggered motion.dev shape.
        isAnimationActive={false}
        style={isClickable || enableHoverHighlight ? { cursor: 'pointer' } : undefined}
        shape={(props: unknown) => (
          <CustomBar {...(props as BarShapeProps)} {...customBarProps} animationType={revealType} />
        )}
        activeBar={(props: unknown) => (
          // The active (hovered) bar must never re-run the grow-in animation
          <CustomBar {...(props as BarShapeProps)} {...customBarProps} animationType="none" />
        )}
        {...barProps}
      />
      <defs>
        <ColorGradient id={id} dataKey={dataKey} config={config} />
        {variant === 'hatched' && <HatchedPattern id={id} dataKey={dataKey} />}
        {variant === 'duotone' && <DuotonePattern id={id} dataKey={dataKey} config={config} />}
        {variant === 'duotone-reverse' && (
          <DuotoneReversePattern id={id} dataKey={dataKey} config={config} />
        )}
        {variant === 'gradient' && <GradientPattern id={id} dataKey={dataKey} />}
        {variant === 'stripped' && <StrippedPattern id={id} dataKey={dataKey} />}
        {bufferBar && <BufferHatchedPattern id={id} dataKey={dataKey} />}
        {glowing && <GlowFilter id={id} dataKey={dataKey} />}
      </defs>
    </>
  )
}

type XAxisProps = ComponentProps<typeof RechartsXAxis>

/**
 * The category axis. Ships with the chart's flat default styling and forwards
 * every Recharts XAxis prop, so `dataKey`, `tickFormatter`, etc. are passed
 * straight through. Hidden automatically while the chart is loading. Resolves
 * its axis type from the chart layout — categorical when vertical, numeric
 * when the bars run horizontally.
 */
function XAxis({
  tickLine = false,
  axisLine = false,
  tickMargin = 8,
  minTickGap = 8,
  type,
  ...props
}: XAxisProps) {
  const { isLoading, isHorizontal } = useBarChart()

  if (isLoading) return null

  return (
    <RechartsXAxis
      tickLine={tickLine}
      axisLine={axisLine}
      tickMargin={tickMargin}
      minTickGap={minTickGap}
      type={type ?? (isHorizontal ? 'number' : 'category')}
      {...props}
    />
  )
}

type YAxisProps = ComponentProps<typeof RechartsYAxis>

/**
 * The value axis. Forwards every Recharts YAxis prop and resolves its axis type
 * from the chart layout — numeric when vertical, categorical when the bars run
 * horizontally. Hidden automatically while the chart is loading.
 */
function YAxis({
  tickLine = false,
  axisLine = false,
  tickMargin = 8,
  minTickGap = 8,
  width = 'auto',
  type,
  ...props
}: YAxisProps) {
  const { isLoading, isHorizontal } = useBarChart()

  if (isLoading) return null

  return (
    <RechartsYAxis
      tickLine={tickLine}
      axisLine={axisLine}
      tickMargin={tickMargin}
      minTickGap={minTickGap}
      width={width}
      type={type ?? (isHorizontal ? 'category' : 'number')}
      {...props}
    />
  )
}

type GridProps = ComponentProps<typeof CartesianGrid>

/**
 * The background grid lines. Defaults to dashed lines aligned to the value
 * axis based on the chart layout, and forwards every Recharts CartesianGrid
 * prop for full control.
 */
function Grid({ strokeDasharray = '3 3', vertical, horizontal, ...props }: GridProps) {
  const { isHorizontal } = useBarChart()

  return (
    <CartesianGrid
      strokeDasharray={strokeDasharray}
      vertical={vertical ?? isHorizontal}
      horizontal={horizontal ?? !isHorizontal}
      {...props}
    />
  )
}

type TooltipProps = {
  variant?: TooltipVariant // visual style of the tooltip surface
  roundness?: TooltipRoundness // border-radius of the tooltip
  defaultIndex?: number // data index shown by default with no hover
  valueFormatter?: (value: number, dataKey: string) => ReactNode // formats each value
}

/**
 * The hover tooltip. Reads the chart's selection from context so its content
 * dims unselected series. Hidden automatically while the chart is loading.
 */
function Tooltip({ variant, roundness, defaultIndex, valueFormatter }: TooltipProps) {
  const { isLoading, selectedDataKey } = useBarChart()

  if (isLoading) return null

  return (
    <ChartTooltip
      cursor={false}
      defaultIndex={defaultIndex}
      content={
        <ChartTooltipContent
          selected={selectedDataKey}
          roundness={roundness}
          variant={variant}
          valueFormatter={valueFormatter}
        />
      }
    />
  )
}

type LegendProps = {
  variant?: ChartLegendVariant // visual style of the legend indicators
  align?: 'left' | 'center' | 'right' // horizontal placement
  verticalAlign?: 'top' | 'middle' | 'bottom' // vertical placement
  isClickable?: boolean // lets each entry toggle selection of its series
}

/**
 * The series legend. When `isClickable` is set, each entry toggles selection of
 * its series, driving the shared selection state read by every <Bar />.
 */
function Legend({
  variant,
  align = 'right',
  verticalAlign = 'top',
  isClickable = false,
}: LegendProps) {
  const { selectedDataKey, selectDataKey } = useBarChart()

  return (
    <ChartLegend
      verticalAlign={verticalAlign}
      align={align}
      content={
        <ChartLegendContent
          selected={selectedDataKey}
          onSelectChange={selectDataKey}
          isClickable={isClickable}
          variant={variant}
        />
      }
    />
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Custom bar shape
// ─────────────────────────────────────────────────────────────────────────────

// Raw geometry Recharts hands to a custom bar shape
type BarShapeProps = {
  x?: number
  y?: number
  width?: number
  height?: number
  fill?: string
  fillOpacity?: number
  dataKey?: string
  index?: number
  [key: string]: unknown
}

// Per-series config the <Bar /> threads into every CustomBar render
type CustomBarProps = {
  id: string
  dataKey: string
  variant: BarVariant
  barRadius: number
  glowing?: boolean
  bufferBar?: boolean
  isClickable?: boolean
  enableHoverHighlight?: boolean
  isMouseInChart?: boolean
  isHorizontal?: boolean
  animationType?: BarAnimationType
  introStartedAt?: number
  selectedDataKey?: string | null
  isActive?: boolean
  dataLength?: number
  expandOf?: (index: number) => number
  onClick?: () => void
} & BarShapeProps

/**
 * Custom bar shape. Renders the visible bar painted by the owning <Bar />'s
 * variant pattern, with an invisible full-height rectangle behind it to keep
 * the whole column hoverable and clickable.
 */
const CustomBar = (props: CustomBarProps) => {
  const {
    x = 0,
    y = 0,
    width = 0,
    height = 0,
    id,
    dataKey,
    variant,
    barRadius,
    glowing,
    bufferBar,
    isClickable,
    enableHoverHighlight,
    isMouseInChart,
    isHorizontal = false,
    animationType = 'none',
    introStartedAt = 0,
    selectedDataKey,
    isActive,
    dataLength = 0,
    expandOf,
    onClick,
  } = props

  const index = typeof props.index === 'number' ? props.index : -1
  const isLastBar = bufferBar && dataLength > 0 && index === dataLength - 1
  const isStripped = variant === 'stripped'
  const grow = getBarGrowAnimation(animationType, index, dataLength, isHorizontal, introStartedAt)

  const fill = isLastBar
    ? `url(#${id}-buffer-hatched-${dataKey})`
    : getVariantFill(variant, id, dataKey)
  const filter = glowing ? `url(#${id}-bar-glow-${dataKey})` : undefined

  const fillOpacity = getBarOpacity({
    isClickable,
    selectedDataKey,
    dataKey,
    enableHoverHighlight,
    isMouseInChart,
    isActive,
  })
  const cursorStyle = isClickable || enableHoverHighlight ? { cursor: 'pointer' } : undefined

  // Stripped bars round only their top corners; expandable rests as a thin line a radius would
  // swallow; every other variant rounds all four
  const isExpandable = variant === 'expandable'
  const radius: RectRadius = isExpandable
    ? 0
    : isStripped
      ? [barRadius, barRadius, 0, 0]
      : barRadius

  // Expandable shrinks the bar across its THICKNESS (width for columns, height for rows)
  // around its centre line; its length along the value axis never changes.
  let paintX = x
  let paintY = y
  let paintWidth = width
  let paintHeight = Math.max(0, height - 3)
  if (isExpandable) {
    const fraction = Math.max(0, Math.min(1, expandOf?.(index) ?? EXPAND_COLLAPSED))
    if (isHorizontal) {
      paintHeight = height * fraction
      paintY = y + (height - paintHeight) / 2
    } else {
      paintWidth = width * fraction
      paintX = x + (width - paintWidth) / 2
      paintHeight = height
    }
  }

  // The visible, painted bar — plus the stripped variant's solid top strip
  const visibleBar = (
    <>
      <Rectangle
        x={paintX}
        y={paintY}
        width={paintWidth}
        opacity={fillOpacity}
        height={paintHeight}
        radius={radius}
        fill={fill}
        filter={filter}
        stroke={isLastBar ? `url(#${id}-colors-${dataKey})` : undefined}
        strokeWidth={isLastBar ? 1 : undefined}
      />
      {isStripped && (
        <Rectangle
          x={x}
          y={y - 4}
          width={width}
          height={2}
          radius={1}
          fill={`url(#${id}-colors-${dataKey})`}
        />
      )}
    </>
  )

  return (
    <g style={cursorStyle} onClick={onClick}>
      {/* Full-height invisible rect keeps the whole column hoverable/clickable */}
      <Rectangle {...props} fill="transparent" />
      {/* The painted bar grows in from its baseline; the hit rect above stays put */}
      {grow ? (
        <motion.g
          initial={grow.initial}
          animate={grow.animate}
          transition={grow.transition}
          style={grow.style}
        >
          {visibleBar}
        </motion.g>
      ) : (
        visibleBar
      )}
    </g>
  )
}

/**
 * Per-bar openness for the `expandable` variant. Recharts remounts every bar shape on each
 * chart re-render, so a CSS transition on the shape would never play — the openness lives
 * here instead and eases toward its target on rAF (exponential approach, EXPAND_TAU), the
 * same model as the ECharts twin. Idle when nothing is moving; snaps under reduced motion.
 */
function useExpandProgress(
  enabled: boolean,
  hovered: number | null,
  reduceMotion: boolean
): ReadonlyMap<number, number> {
  const [progress, setProgress] = useState<ReadonlyMap<number, number>>(() => new Map())
  const progressRef = useRef(progress)

  useEffect(() => {
    if (!enabled) return
    const step = (current: ReadonlyMap<number, number>, k: number) => {
      const next = new Map<number, number>()
      let moving = false
      const indices = new Set(current.keys())
      if (hovered !== null) indices.add(hovered)
      for (const i of indices) {
        const target = i === hovered ? 1 : EXPAND_COLLAPSED
        const from = current.get(i) ?? EXPAND_COLLAPSED
        const value = from + (target - from) * k
        if (Math.abs(target - value) > 0.004) {
          moving = true
          next.set(i, value)
        } else if (target !== EXPAND_COLLAPSED) {
          next.set(i, target)
        }
      }
      progressRef.current = next
      setProgress(next)
      return moving
    }

    if (reduceMotion) {
      step(progressRef.current, 1)
      return
    }
    let raf = 0
    let last = performance.now()
    const tick = (now: number) => {
      const k = 1 - Math.exp(-(now - last) / EXPAND_TAU)
      last = now
      if (step(progressRef.current, k)) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [enabled, hovered, reduceMotion])

  return progress
}

/**
 * Builds the motion.dev grow-in animation for a single bar, or returns `null`
 * when the bar should render statically (`"none"`, reduced motion, an unknown
 * index, or — crucially — once the bar has already finished growing).
 *
 * Every bar grows from its baseline — `scaleY` from the bottom for vertical
 * layout, `scaleX` from the left for horizontal — and `animationType` decides
 * the stagger order, so the chart fills in one bar at a time.
 *
 * The intro is anchored to `introStartedAt` (stamped once when the chart
 * mounts) rather than to component mount. Recharts remounts every bar whenever
 * the chart re-renders — e.g. on hover-highlight — so a mount-based animation
 * would replay endlessly. Reading elapsed time instead makes it a true
 * one-shot: a bar past its window renders static, and a bar caught mid-grow
 * resumes from the progress it should already be at.
 */
const getBarGrowAnimation = (
  animationType: BarAnimationType,
  index: number,
  dataLength: number,
  isHorizontal: boolean,
  introStartedAt: number
) => {
  if (animationType === 'none' || index < 0 || dataLength <= 0) return null

  const lastIndex = dataLength - 1
  const center = lastIndex / 2

  // How many bars this one waits behind before it starts growing
  let step: number
  switch (animationType) {
    case 'right-to-left':
      step = lastIndex - index
      break
    case 'center-out':
      step = Math.abs(index - center)
      break
    case 'edges-in':
      step = center - Math.abs(index - center)
      break
    default: // left-to-right
      step = index
  }

  const startMs = step * BAR_STAGGER * 1000
  const durationMs = BAR_GROW_DURATION * 1000
  const endMs = startMs + durationMs
  const elapsed = Date.now() - introStartedAt

  // Already finished — render static so re-renders/remounts can't replay it
  if (elapsed >= endMs) return null

  // Resume from wherever this bar should already be: 0 before it starts,
  // partway through if a remount caught it mid-grow.
  const from = elapsed <= startMs ? 0 : (elapsed - startMs) / durationMs
  const transition = {
    duration: (endMs - Math.max(elapsed, startMs)) / 1000,
    ease: REVEAL_EASE,
    delay: Math.max(0, startMs - elapsed) / 1000,
  }

  // Horizontal bars grow rightward from the left edge, vertical from the bottom
  return isHorizontal
    ? { initial: { scaleX: from }, animate: { scaleX: 1 }, transition, style: { originX: 0 } }
    : { initial: { scaleY: from }, animate: { scaleY: 1 }, transition, style: { originY: 1 } }
}

// ─────────────────────────────────────────────────────────────────────────────
// Selection + fill helpers
// ─────────────────────────────────────────────────────────────────────────────

// Resolves the SVG paint reference for a bar's fill based on its variant
const getVariantFill = (variant: BarVariant, id: string, dataKey: string): string => {
  switch (variant) {
    case 'hatched':
      return `url(#${id}-hatched-${dataKey})`
    case 'duotone':
      return `url(#${id}-duotone-${dataKey})`
    case 'duotone-reverse':
      return `url(#${id}-duotone-reverse-${dataKey})`
    case 'gradient':
      return `url(#${id}-gradient-${dataKey})`
    case 'stripped':
      return `url(#${id}-stripped-${dataKey})`
    default:
      return `url(#${id}-colors-${dataKey})`
  }
}

// Computes bar opacity from the click selection and hover-highlight state
const getBarOpacity = ({
  isClickable,
  selectedDataKey,
  dataKey,
  enableHoverHighlight,
  isMouseInChart,
  isActive,
}: {
  isClickable?: boolean
  selectedDataKey?: string | null
  dataKey: string
  enableHoverHighlight?: boolean
  isMouseInChart?: boolean
  isActive?: boolean
}) => {
  const isSelectedDataKey = selectedDataKey === null || selectedDataKey === dataKey
  const clickOpacity = isClickable && selectedDataKey !== null ? (isSelectedDataKey ? 1 : 0.15) : 1

  // While hovering, the hovered bar keeps its click opacity and the rest dim further
  if (enableHoverHighlight && isMouseInChart) {
    return isActive ? clickOpacity : clickOpacity * 0.3
  }

  return clickOpacity
}

// ─────────────────────────────────────────────────────────────────────────────
// Style definitions — one set per <Bar />, scoped to its unique id
// ─────────────────────────────────────────────────────────────────────────────

type StyleProps = {
  id: string // unique id of the owning <Bar />
  dataKey: string // series key the colors belong to
}

/**
 * Vertical top-to-bottom color gradient for a series. Always rendered — every
 * fill variant and the buffer-bar stroke paint from this single gradient.
 */
const ColorGradient = ({ id, dataKey, config }: StyleProps & { config: ChartConfig }) => {
  const colorsCount = getColorsCount(config[dataKey] ?? {})

  return (
    <linearGradient id={`${id}-colors-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
      {colorsCount === 1 ? (
        <>
          <stop offset="0%" stopColor={`var(--color-${dataKey}-0)`} />
          <stop offset="100%" stopColor={`var(--color-${dataKey}-0)`} />
        </>
      ) : (
        Array.from({ length: colorsCount }, (_, index) => {
          const offset = `${(index / (colorsCount - 1)) * 100}%`
          return (
            <stop
              key={offset}
              offset={offset}
              stopColor={`var(--color-${dataKey}-${index}, var(--color-${dataKey}-0))`}
            />
          )
        })
      )}
    </linearGradient>
  )
}

/** Diagonal hatched-stripe fill, masked from the series color gradient. */
const HatchedPattern = ({ id, dataKey }: StyleProps) => {
  return (
    <>
      <pattern
        id={`${id}-hatched-mask-pattern`}
        x="0"
        y="0"
        width="5"
        height="5"
        patternUnits="userSpaceOnUse"
        patternTransform="rotate(-45)"
      >
        <rect width="5" height="5" fill="white" fillOpacity={0.3} />
        <rect width="1.5" height="5" fill="white" fillOpacity={1} />
      </pattern>
      <mask id={`${id}-hatched-mask-${dataKey}`}>
        <rect width="100%" height="100%" fill={`url(#${id}-hatched-mask-pattern)`} />
      </mask>
      <pattern
        id={`${id}-hatched-${dataKey}`}
        patternUnits="userSpaceOnUse"
        width="100%"
        height="100%"
      >
        <rect
          width="100%"
          height="100%"
          fill={`url(#${id}-colors-${dataKey})`}
          mask={`url(#${id}-hatched-mask-${dataKey})`}
        />
      </pattern>
    </>
  )
}

/** Hatched diagonal lines with no background fill, used for the buffer bar. */
const BufferHatchedPattern = ({ id, dataKey }: StyleProps) => {
  return (
    <>
      <pattern
        id={`${id}-buffer-hatched-mask-pattern`}
        x="0"
        y="0"
        width="5"
        height="5"
        patternUnits="userSpaceOnUse"
        patternTransform="rotate(-45)"
      >
        <rect width="5" height="5" fill="black" fillOpacity={0} />
        <rect width="1" height="5" fill="white" fillOpacity={1} />
      </pattern>
      <mask id={`${id}-buffer-hatched-mask-${dataKey}`}>
        <rect width="100%" height="100%" fill={`url(#${id}-buffer-hatched-mask-pattern)`} />
      </mask>
      <pattern
        id={`${id}-buffer-hatched-${dataKey}`}
        patternUnits="userSpaceOnUse"
        width="100%"
        height="100%"
      >
        <rect
          width="100%"
          height="100%"
          fill={`url(#${id}-colors-${dataKey})`}
          mask={`url(#${id}-buffer-hatched-mask-${dataKey})`}
        />
      </pattern>
    </>
  )
}

/** Two-tone fill — a half-faded, half-solid split applied per bar bounding box. */
const DuotonePattern = ({ id, dataKey, config }: StyleProps & { config: ChartConfig }) => {
  const colorsCount = getColorsCount(config[dataKey] ?? {})

  return (
    <>
      <linearGradient
        id={`${id}-duotone-mask-gradient-${dataKey}`}
        gradientUnits="objectBoundingBox"
        x1="0"
        y1="0"
        x2="1"
        y2="0"
      >
        <stop offset="50%" stopColor="white" stopOpacity={0.4} />
        <stop offset="50%" stopColor="white" stopOpacity={1} />
      </linearGradient>
      <linearGradient
        id={`${id}-duotone-colors-${dataKey}`}
        gradientUnits="objectBoundingBox"
        x1="0"
        y1="0"
        x2="0"
        y2="1"
      >
        {colorsCount === 1 ? (
          <>
            <stop offset="0%" stopColor={`var(--color-${dataKey}-0)`} />
            <stop offset="100%" stopColor={`var(--color-${dataKey}-0)`} />
          </>
        ) : (
          Array.from({ length: colorsCount }, (_, index) => {
            const offset = `${(index / (colorsCount - 1)) * 100}%`
            return (
              <stop
                key={offset}
                offset={offset}
                stopColor={`var(--color-${dataKey}-${index}, var(--color-${dataKey}-0))`}
              />
            )
          })
        )}
      </linearGradient>
      <mask id={`${id}-duotone-mask-${dataKey}`} maskContentUnits="objectBoundingBox">
        <rect
          x="0"
          y="0"
          width="1"
          height="1"
          fill={`url(#${id}-duotone-mask-gradient-${dataKey})`}
        />
      </mask>
      <pattern
        id={`${id}-duotone-${dataKey}`}
        patternUnits="objectBoundingBox"
        patternContentUnits="objectBoundingBox"
        width="1"
        height="1"
      >
        <rect
          x="0"
          y="0"
          width="1"
          height="1"
          fill={`url(#${id}-duotone-colors-${dataKey})`}
          mask={`url(#${id}-duotone-mask-${dataKey})`}
        />
      </pattern>
    </>
  )
}

/** Two-tone fill with the solid and faded halves reversed from `duotone`. */
const DuotoneReversePattern = ({ id, dataKey, config }: StyleProps & { config: ChartConfig }) => {
  const colorsCount = getColorsCount(config[dataKey] ?? {})

  return (
    <>
      <linearGradient
        id={`${id}-duotone-reverse-mask-gradient-${dataKey}`}
        gradientUnits="objectBoundingBox"
        x1="0"
        y1="0"
        x2="1"
        y2="0"
      >
        <stop offset="50%" stopColor="white" stopOpacity={1} />
        <stop offset="50%" stopColor="white" stopOpacity={0.4} />
      </linearGradient>
      <linearGradient
        id={`${id}-duotone-reverse-colors-${dataKey}`}
        gradientUnits="objectBoundingBox"
        x1="0"
        y1="0"
        x2="0"
        y2="1"
      >
        {colorsCount === 1 ? (
          <>
            <stop offset="0%" stopColor={`var(--color-${dataKey}-0)`} />
            <stop offset="100%" stopColor={`var(--color-${dataKey}-0)`} />
          </>
        ) : (
          Array.from({ length: colorsCount }, (_, index) => {
            const offset = `${(index / (colorsCount - 1)) * 100}%`
            return (
              <stop
                key={offset}
                offset={offset}
                stopColor={`var(--color-${dataKey}-${index}, var(--color-${dataKey}-0))`}
              />
            )
          })
        )}
      </linearGradient>
      <mask id={`${id}-duotone-reverse-mask-${dataKey}`} maskContentUnits="objectBoundingBox">
        <rect
          x="0"
          y="0"
          width="1"
          height="1"
          fill={`url(#${id}-duotone-reverse-mask-gradient-${dataKey})`}
        />
      </mask>
      <pattern
        id={`${id}-duotone-reverse-${dataKey}`}
        patternUnits="objectBoundingBox"
        patternContentUnits="objectBoundingBox"
        width="1"
        height="1"
      >
        <rect
          x="0"
          y="0"
          width="1"
          height="1"
          fill={`url(#${id}-duotone-reverse-colors-${dataKey})`}
          mask={`url(#${id}-duotone-reverse-mask-${dataKey})`}
        />
      </pattern>
    </>
  )
}

/** Gradient fill that fades the series color from solid at the top to clear. */
const GradientPattern = ({ id, dataKey }: StyleProps) => {
  return (
    <>
      <linearGradient id={`${id}-gradient-mask-gradient`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="20%" stopColor="white" stopOpacity={1} />
        <stop offset="90%" stopColor="white" stopOpacity={0} />
      </linearGradient>
      <mask id={`${id}-gradient-mask-${dataKey}`}>
        <rect width="100%" height="100%" fill={`url(#${id}-gradient-mask-gradient)`} />
      </mask>
      <pattern
        id={`${id}-gradient-${dataKey}`}
        patternUnits="userSpaceOnUse"
        width="100%"
        height="100%"
      >
        <rect
          width="100%"
          height="100%"
          fill={`url(#${id}-colors-${dataKey})`}
          mask={`url(#${id}-gradient-mask-${dataKey})`}
        />
      </pattern>
    </>
  )
}

/** Low-opacity body fill, paired with a solid top strip drawn by CustomBar. */
const StrippedPattern = ({ id, dataKey }: StyleProps) => {
  return (
    <>
      <linearGradient id={`${id}-stripped-mask-gradient`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="white" stopOpacity={0.2} />
        <stop offset="100%" stopColor="white" stopOpacity={0.2} />
      </linearGradient>
      <mask id={`${id}-stripped-mask-${dataKey}`}>
        <rect width="100%" height="100%" fill={`url(#${id}-stripped-mask-gradient)`} />
      </mask>
      <pattern
        id={`${id}-stripped-${dataKey}`}
        patternUnits="userSpaceOnUse"
        width="100%"
        height="100%"
      >
        <rect
          width="100%"
          height="100%"
          fill={`url(#${id}-colors-${dataKey})`}
          mask={`url(#${id}-stripped-mask-${dataKey})`}
        />
      </pattern>
    </>
  )
}

/** Soft outer-glow filter applied to a glowing bar. */
const GlowFilter = ({ id, dataKey }: StyleProps) => {
  return (
    <filter id={`${id}-bar-glow-${dataKey}`} x="-100%" y="-100%" width="300%" height="300%">
      <feGaussianBlur in="SourceGraphic" stdDeviation="8" result="blur" />
      <feColorMatrix
        in="blur"
        type="matrix"
        values="1 0 0 0 0
                0 1 0 0 0
                0 0 1 0 0
                0 0 0 0.5 0"
        result="glow"
      />
      <feMerge>
        <feMergeNode in="glow" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Loading skeleton
// ─────────────────────────────────────────────────────────────────────────────

// Builds bell-curve eased gradient stops for the loading shimmer
const generateEasedGradientStops = (
  steps: number = 17,
  minOpacity: number = 0.05,
  maxOpacity: number = 0.9
) => {
  return Array.from({ length: steps }, (_, i) => {
    const t = i / (steps - 1) // 0 to 1
    // Sine-based bell curve easing: peaks at center (t=0.5), smooth falloff at edges
    const eased = Math.sin(t * Math.PI) ** 2
    const opacity = minOpacity + eased * (maxOpacity - minOpacity)
    return { offset: `${(t * 100).toFixed(0)}%`, opacity: Number(opacity.toFixed(3)) }
  })
}

/**
 * Hook to manage loading data with pixel-perfect shimmer synchronization.
 *
 * Uses motion.dev's onUpdate callback to ensure chart data is only regenerated
 * when the shimmer has completely exited the visible area. This eliminates
 * timing drift issues from setTimeout/setInterval.
 */
export function useLoadingData(isLoading: boolean, loadingBars: number = 12) {
  const [loadingDataKey, setLoadingDataKey] = useState(false)

  // Callback fired by motion.dev when the shimmer exits the visible area
  const onShimmerExit = useCallback(() => {
    if (isLoading) {
      setLoadingDataKey((prev) => !prev)
    }
  }, [isLoading])

  const loadingData = useMemo(
    () => getLoadingData(loadingBars, 20, 80),
    // loadingDataKey toggle triggers re-computation when the shimmer exits
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadingBars, loadingDataKey]
  )

  return { loadingData, onShimmerExit }
}

/**
 * The skeleton bar shown while the chart is loading. Rendered by the root in
 * place of the real bars, paired with its own masked shimmer pattern.
 */
const LoadingBar = ({ chartId, onShimmerExit }: { chartId: string; onShimmerExit: () => void }) => {
  return (
    <>
      <RechartsBar
        dataKey={LOADING_BAR_DATA_KEY}
        fill="currentColor"
        fillOpacity={0.15}
        radius={DEFAULT_BAR_RADIUS}
        isAnimationActive={false}
        legendType="none"
        style={{ mask: `url(#${chartId}-loading-mask)` }}
      />
      <defs>
        <LoadingBarPattern chartId={chartId} onShimmerExit={onShimmerExit} />
      </defs>
    </>
  )
}

/**
 * Animated shimmer pattern for the loading skeleton.
 *
 * The visible chart area is normalized to 0-1, the shimmer gradient has width 1,
 * and the pattern is 3x wide so the shimmer has buffer on both sides. The motion
 * rect travels x from -1 to 2; onShimmerExit fires as it crosses x=1, letting the
 * data swap happen while the shimmer is off-screen for a seamless loop.
 */
const LoadingBarPattern = ({
  chartId,
  onShimmerExit,
}: {
  chartId: string
  onShimmerExit: () => void
}) => {
  const gradientStops = generateEasedGradientStops()

  // 1 (left buffer) + 1 (visible) + 1 (right buffer)
  const patternWidth = 3
  const startX = -1
  const endX = 2

  // Tracks the last x value to detect the exit threshold crossing
  const lastXRef = useRef(startX)

  return (
    <>
      <linearGradient id={`${chartId}-loading-mask-gradient`} x1="0" y1="0" x2="1" y2="0">
        {gradientStops.map(({ offset, opacity }) => (
          <stop key={offset} offset={offset} stopColor="white" stopOpacity={opacity} />
        ))}
      </linearGradient>
      <pattern
        id={`${chartId}-loading-mask-pattern`}
        patternUnits="objectBoundingBox"
        patternContentUnits="objectBoundingBox"
        patternTransform="rotate(25)"
        width={patternWidth}
        height="1"
        x="0"
        y="0"
      >
        <motion.rect
          y="0"
          width="1"
          height="1"
          fill={`url(#${chartId}-loading-mask-gradient)`}
          initial={{ x: startX }}
          animate={{ x: endX }}
          transition={{
            duration: LOADING_ANIMATION_DURATION / 1000,
            ease: 'linear',
            repeat: Infinity,
            repeatType: 'loop',
          }}
          onUpdate={(latest) => {
            const xValue = typeof latest.x === 'number' ? latest.x : startX
            const lastX = lastXRef.current

            // Fire once per loop, when the shimmer fully exits the visible area
            if (xValue >= 1 && lastX < 1) {
              onShimmerExit()
            }

            lastXRef.current = xValue
          }}
        />
      </pattern>
      <mask id={`${chartId}-loading-mask`} maskUnits="userSpaceOnUse">
        <rect width="100%" height="100%" fill={`url(#${chartId}-loading-mask-pattern)`} />
      </mask>
    </>
  )
}

// Compound API: every part hangs off the root as a static member, so a consumer
// writes <EvilBarChart.Bar/>, <EvilBarChart.Tooltip/>, … from a single import
// — no colliding named marker exports when several charts share one file.
EvilBarChart.Bar = Bar
EvilBarChart.XAxis = XAxis
EvilBarChart.YAxis = YAxis
EvilBarChart.Grid = Grid
EvilBarChart.Tooltip = Tooltip
EvilBarChart.Legend = Legend
EvilBarChart.Brush = Brush
